import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Shield } from "lucide-react";
import { BondLockup } from "../components/BondMark";
import {
  clearError,
  loginUser,
  selectAuthError,
  selectAuthLoading,
  selectIsAdmin,
  selectIsAuthenticated,
} from "../redux/slices/authSlice";
import { getMainSiteUrl } from "../utils/adminHost";
import "./admin.css";

/**
 * Staff-only login for admin.betbond.app.
 * Uses website-admin auth only — ignores any consumer (betbond.app) app session.
 */
export default function AdminLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  if (isAuthenticated && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    dispatch(clearError());

    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedPassword = password.trim();

    if (!sanitizedEmail || !sanitizedEmail.includes("@")) {
      setLocalError("Enter a valid email.");
      return;
    }
    if (sanitizedPassword.length < 6) {
      setLocalError("Password must be at least 6 characters.");
      return;
    }

    try {
      await dispatch(
        loginUser({ email: sanitizedEmail, password: sanitizedPassword })
      ).unwrap();
      navigate("/admin", { replace: true });
    } catch {
      /* error shown from slice */
    }
  };

  const displayError = localError || error;

  return (
    <div className="admin-login">
      <div className="admin-login-aurora" aria-hidden>
        <div className="admin-login-aurora-a" />
        <div className="admin-login-aurora-b" />
      </div>

      <div className="admin-login-grid">
        {/* Brand panel */}
        <div className="admin-login-brand">
          <BondLockup size={28} textClassName="text-2xl" />

          <div className="admin-login-brand-body">
            <p className="admin-login-eyebrow">
              <Shield size={12} strokeWidth={2.5} />
              Staff access only
            </p>
            <h1 className="admin-login-headline">
              Admin <span>control</span> center
            </h1>
            <p className="admin-login-tagline">
              Monitor P2P desks, treasury, users, and platform health from one
              mission-control dashboard.
            </p>

            <ul className="admin-login-features">
              <li>Real-time platform metrics</li>
              <li>Payment &amp; treasury oversight</li>
              <li>User management &amp; moderation</li>
            </ul>
          </div>

          <p className="admin-login-footer-note">© BetBond · Built in Rwanda</p>
        </div>

        {/* Form panel */}
        <div className="admin-login-form-wrap">
          <div className="admin-login-form-card">
            <div className="admin-login-form-head">
              <div className="lg:hidden mb-6">
                <BondLockup size={24} textClassName="text-xl" />
              </div>
              <h2 className="admin-login-form-title">Sign in</h2>
              <p className="admin-login-form-sub">Use your staff admin credentials.</p>
            </div>

            <form className="admin-login-form" onSubmit={handleSubmit}>
              {displayError ? (
                <div className="admin-login-error" role="alert">
                  {displayError}
                </div>
              ) : null}

              <div className="admin-login-field">
                <label htmlFor="admin-email" className="admin-login-label">
                  Email
                </label>
                <input
                  id="admin-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) dispatch(clearError());
                    if (localError) setLocalError("");
                  }}
                  className="admin-login-input"
                  placeholder="admin@betbond.app"
                />
              </div>

              <div className="admin-login-field">
                <label htmlFor="admin-password" className="admin-login-label">
                  Password
                </label>
                <div className="admin-login-password-wrap">
                  <input
                    id="admin-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) dispatch(clearError());
                      if (localError) setLocalError("");
                    }}
                    className="admin-login-input"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="admin-login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="admin-login-submit">
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="admin-sync-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in to admin"
                )}
              </button>
            </form>
          </div>

          <p className="admin-login-back">
            <a href={getMainSiteUrl("/")}>← Back to BetBond</a>
          </p>
        </div>
      </div>
    </div>
  );
}
